import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle, FileText, Home, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { ProcessingTimeline } from '@/components/papers/processing/ProcessingTimeline';

const UploadSuccess = () => {
  const { paperId } = useParams<{ paperId: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    // If no paperId, redirect to upload page
    if (!paperId) {
      navigate('/upload');
    }
  }, [paperId, navigate]);

  if (!paperId) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="max-w-2xl mx-auto text-center space-y-8"
        >
          {/* Success Icon with Animation */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5, type: "spring", stiffness: 200 }}
            className="flex justify-center"
          >
            <CheckCircle className="h-24 w-24 text-green-500" />
          </motion.div>

          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="space-y-4"
          >
            <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
              Paper Successfully Linked!
            </h1>
            <p className="text-xl text-muted-foreground max-w-lg mx-auto">
              Your research paper has been successfully added to the platform and is ready for the community to discover.
            </p>
          </motion.div>

          {/* Live processing timeline */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            <ProcessingTimeline paperId={paperId!} variant="full" />
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.5 }}
            className="flex flex-col sm:flex-row gap-4 justify-center pt-4"
          >
            <Button
              asChild
              variant="outline"
              size="lg"
              className="group hover:bg-accent/50 transition-all duration-200"
            >
              <Link to="/" className="flex items-center gap-2">
                <Home className="h-4 w-4" />
                Back to Home
              </Link>
            </Button>
            
            <Button
              asChild
              size="lg"
              className="group bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-200"
            >
              <Link to={`/paper/${paperId}`} className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                View Paper
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
            </Button>
          </motion.div>

          {/* Additional Context */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 0.5 }}
            className="pt-8"
          >
            <p className="text-sm text-muted-foreground">
              Your paper is now part of our research community. 
              <br />
              Share it with colleagues and start receiving valuable feedback!
            </p>
          </motion.div>
        </motion.div>
      </main>
    </div>
  );
};

export default UploadSuccess;